import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import SaveIcon from '@mui/icons-material/Save';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import AlertModal from '../components/AlertModal';
import PremiumSelect from '../components/PremiumSelect';
import PageContainer from '../components/ui/PageContainer';
import StatusChip from '../components/ui/StatusChip';
import BehavioralChartsPreview from '../components/BehavioralChartsPreview';
import { User, AlertState } from '../types';
import {
  DISC_SERIES,
  VALORES_COMPORTAMENTAIS,
  TRAIT_ROWS,
  BehavioralFormKey,
} from '../constants/userBehavioral';

type FormState = Record<BehavioralFormKey, string>;

const emptyForm = (): FormState => {
  const keys: BehavioralFormKey[] = [
    ...DISC_SERIES.flatMap((d) => [d.key, d.adaptedKey]),
    ...VALORES_COMPORTAMENTAIS.map((v) => v.key),
    ...TRAIT_ROWS.flatMap((r) => [r.natural, r.adapted]),
  ];
  return keys.reduce((acc, k) => {
    acc[k] = '';
    return acc;
  }, {} as FormState);
};

const userToForm = (u: User): FormState => {
  const f = emptyForm();
  const assign = (key: BehavioralFormKey): void => {
    const v = u[key as keyof User];
    if (v !== undefined && v !== null && typeof v === 'number') {
      f[key] = String(v);
    }
  };
  DISC_SERIES.forEach((d) => {
    assign(d.key);
    assign(d.adaptedKey);
  });
  VALORES_COMPORTAMENTAIS.forEach((v) => assign(v.key));
  TRAIT_ROWS.forEach((r) => {
    assign(r.natural);
    assign(r.adapted);
  });
  return f;
};

/** Normaliza separador decimal (`,` ou `.`), incl. formato tipo 1.234,56. */
function normalizeDecimalSeparators(raw: string): string {
  const t = raw.trim().replace(/\s/g, '');
  if (t === '') return '';
  const lastComma = t.lastIndexOf(',');
  const lastDot = t.lastIndexOf('.');
  if (lastComma >= 0 && lastDot >= 0) {
    if (lastComma > lastDot) {
      return t.replace(/\./g, '').replace(',', '.');
    }
    return t.replace(/,/g, '');
  }
  if (lastComma >= 0) return t.replace(',', '.');
  return t;
}

/** Parse valor do formulário: ≥ 0, máx. 9999, vírgula ou ponto como decimal. */
function parseNonNegativeBehavioralNumber(raw: string): number | undefined {
  const t = raw.trim();
  if (t === '') return undefined;
  const norm = normalizeDecimalSeparators(t);
  if (norm === '' || norm === '.' || norm === '-') return undefined;
  const n = Number.parseFloat(norm);
  if (!Number.isFinite(n) || n < 0 || n > 9999) return undefined;
  return n;
}

const BehavioralAdmin: React.FC = () => {
  const authContext = useContext(AuthContext);
  const { user } = authContext || {};
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string>('');
  const [form, setForm] = useState<FormState>(emptyForm);
  const [alert, setAlert] = useState<AlertState>({ isOpen: false, message: '', type: 'error' });
  /** Acordeão do cadastro: recolhido deixa só os gráficos em destaque. */
  const [cadastroExpanded, setCadastroExpanded] = useState<boolean>(true);

  const loadUsers = useCallback(async (): Promise<void> => {
    try {
      const { data } = await api.get<User[]>('/users');
      setUsers(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'hr') {
      void loadUsers();
    }
  }, [loadUsers, user]);

  const selectedUser = useMemo(
    () => users.find((u) => u.id === selectedId) ?? null,
    [users, selectedId],
  );

  useEffect(() => {
    if (selectedUser) {
      setForm(userToForm(selectedUser));
    } else {
      setForm(emptyForm());
    }
  }, [selectedUser]);

  const handleField = (key: BehavioralFormKey, value: string): void => {
    const t = value.replace(/\s/g, '');
    if (t !== '' && !/^\d*([.,]\d*)?$/.test(t)) return;
    if (t.length > 16) return;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!selectedId) {
      setAlert({ isOpen: true, message: 'Selecione um usuário.', type: 'warning' });
      return;
    }

    const payload: Record<string, number> = {};
    const keys: BehavioralFormKey[] = [
      ...DISC_SERIES.flatMap((d) => [d.key, d.adaptedKey]),
      ...VALORES_COMPORTAMENTAIS.map((v) => v.key),
      ...TRAIT_ROWS.flatMap((r) => [r.natural, r.adapted]),
    ];
    for (const key of keys) {
      const n = parseNonNegativeBehavioralNumber(form[key]);
      if (n !== undefined) payload[key] = n;
    }

    if (Object.keys(payload).length === 0) {
      setAlert({
        isOpen: true,
        message: 'Preencha ao menos um campo numérico para salvar.',
        type: 'warning',
      });
      return;
    }

    try {
      const { data } = await api.patch<User>(`/users/${selectedId}/behavioral`, payload);
      setUsers((prev) => prev.map((u) => (u.id === data.id ? { ...u, ...data } : u)));
      setForm(userToForm(data));
      setAlert({ isOpen: true, message: 'Perfil comportamental salvo.', type: 'success' });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao salvar.';
      setAlert({ isOpen: true, message: msg, type: 'error' });
    }
  };

  if (!user || (user.role !== 'admin' && user.role !== 'hr')) {
    return (
      <PageContainer maxWidth="md">
        <Paper variant="outlined" sx={{ p: { xs: 3, md: 4 }, textAlign: 'center' }}>
          <Typography variant="h6" component="p">
            Acesso restrito a administradores ou RH.
          </Typography>
        </Paper>
      </PageContainer>
    );
  }

  if (loading) {
    return (
      <PageContainer>
        <Typography color="text.secondary">Carregando usuários…</Typography>
      </PageContainer>
    );
  }

  const numberFieldProps = {
    type: 'number' as const,
    fullWidth: true,
    size: 'small' as const,
    slotProps: {
      htmlInput: { inputMode: 'decimal' as const, min: 0, max: 9999, step: 'any' },
    },
  };

  return (
    <PageContainer
      title="Perfil comportamental e DISC"
      subtitle="Cadastre ou atualize a análise DISC, valores comportamentais e escalas natural / adaptado para cada colaborador."
      icon={<PsychologyOutlinedIcon />}
      actions={<StatusChip label="Administração" tone="accent" />}
    >
      <Stack spacing={{ xs: 2, md: 3 }}>
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
          <PremiumSelect
            label="Usuário"
            value={selectedId}
            options={[
              { value: '', label: 'Selecione…' },
              ...users.map((u) => ({ value: u.id, label: `${u.name} (${u.email})` })),
            ]}
            onChange={(v) => setSelectedId(v)}
          />

          {!selectedId ? (
            <Typography color="text.secondary" sx={{ mt: 2 }}>
              Escolha um usuário para editar ou visualizar os gráficos.
            </Typography>
          ) : (
            <Accordion
              expanded={cadastroExpanded}
              onChange={() => setCadastroExpanded((prev) => !prev)}
              disableGutters
              square
              elevation={0}
              sx={{ mt: 2, border: 'none', '&::before': { display: 'none' } }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="behavioral-cadastro-panel"
                id="behavioral-cadastro-trigger"
                sx={{ px: 0 }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 700 }}>Cadastro de dados</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {cadastroExpanded
                      ? 'Recolher para ver apenas os gráficos'
                      : 'Expandir para alterar ou cadastrar valores'}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 0 }}>
                <Box component="form" onSubmit={handleSubmit}>
                  <Stack spacing={{ xs: 3, md: 4 }}>
                    {/* DISC */}
                    <Box>
                      <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
                        DISC
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Perfil (D, I, S, C) e coluna adaptado — use números ≥ 0; decimais com
                        vírgula ou ponto (ex.: 12,5 ou 12.5).
                      </Typography>
                      <Stack spacing={2}>
                        {DISC_SERIES.map((d) => (
                          <Grid container spacing={2} key={d.key} sx={{ alignItems: 'center' }}>
                            <Grid size={{ xs: 12, md: 4 }}>
                              <Typography sx={{ fontWeight: 600, color: d.fill }}>
                                {d.short} — {d.label}
                              </Typography>
                            </Grid>
                            <Grid size={{ xs: 6, md: 4 }}>
                              <TextField
                                {...numberFieldProps}
                                label="Perfil"
                                aria-label={`DISC ${d.short} perfil`}
                                value={form[d.key]}
                                onChange={(e) => handleField(d.key, e.target.value)}
                                placeholder="0"
                              />
                            </Grid>
                            <Grid size={{ xs: 6, md: 4 }}>
                              <TextField
                                {...numberFieldProps}
                                label="Adaptado"
                                aria-label={`DISC ${d.short} adaptado`}
                                value={form[d.adaptedKey]}
                                onChange={(e) => handleField(d.adaptedKey, e.target.value)}
                                placeholder="0"
                              />
                            </Grid>
                          </Grid>
                        ))}
                      </Stack>
                    </Box>

                    {/* Valores comportamentais */}
                    <Box>
                      <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 2 }}>
                        Valores comportamentais
                      </Typography>
                      <Grid container spacing={2}>
                        {VALORES_COMPORTAMENTAIS.map((v) => (
                          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={v.key}>
                            <TextField
                              {...numberFieldProps}
                              label={v.label}
                              value={form[v.key]}
                              onChange={(e) => handleField(v.key, e.target.value)}
                              placeholder="0"
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>

                    {/* Competências — natural e adaptado */}
                    <Box>
                      <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
                        Competências — natural e adaptado
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Para cada dimensão, informe o valor natural e o adaptado (decimais com
                        vírgula ou ponto).
                      </Typography>
                      <Stack spacing={2}>
                        {TRAIT_ROWS.map((row) => (
                          <Grid container spacing={2} key={row.label} sx={{ alignItems: 'center' }}>
                            <Grid size={{ xs: 12, md: 4 }}>
                              <Typography sx={{ fontWeight: 600 }}>{row.label}</Typography>
                            </Grid>
                            <Grid size={{ xs: 6, md: 4 }}>
                              <TextField
                                {...numberFieldProps}
                                label="Natural"
                                aria-label={`${row.label} natural`}
                                value={form[row.natural]}
                                onChange={(e) => handleField(row.natural, e.target.value)}
                              />
                            </Grid>
                            <Grid size={{ xs: 6, md: 4 }}>
                              <TextField
                                {...numberFieldProps}
                                label="Adaptado"
                                aria-label={`${row.label} adaptado`}
                                value={form[row.adapted]}
                                onChange={(e) => handleField(row.adapted, e.target.value)}
                              />
                            </Grid>
                          </Grid>
                        ))}
                      </Stack>
                    </Box>

                    <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
                      <Button type="submit" variant="contained" startIcon={<SaveIcon />}>
                        Salvar perfil
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              </AccordionDetails>
            </Accordion>
          )}
        </Paper>

        {selectedId ? (
          <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 2 }}>
              Perfil comportamental — visualização
            </Typography>
            <BehavioralChartsPreview user={selectedUser} showHeader={false} />
          </Paper>
        ) : null}
      </Stack>

      <AlertModal
        isOpen={alert.isOpen}
        onClose={() => setAlert({ ...alert, isOpen: false })}
        message={alert.message}
        type={alert.type}
      />
    </PageContainer>
  );
};

export default BehavioralAdmin;
